import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

// Tipos para admin user
export interface AdminUser {
  id: string
  email: string
  full_name: string | null
  status: string
  is_admin: boolean
  created_at: string
  updated_at: string
  approved_at: string | null
  rejected_at: string | null
}

export interface AdminDashboard {
  total_users: number
  pending_count: number
  approved_count: number
  rejected_count: number
}

export interface ApprovalToken {
  id: string
  user_id: string
  token: string
  action: 'approve' | 'reject'
  expires_at: string
  used_at: string | null
  created_at: string
  user: AdminUser
}

// Hooks para admin

export function usePendingUsers() {
  return useQuery({
    queryKey: ['admin', 'pending-users'] as const,
    queryFn: async () => {
      const res = await api.get<AdminUser[]>('/v0.1/admin/pending-registrations')
      return res.data
    },
    staleTime: 30_000,
  })
}

export function useAdminDashboard() {
  return useQuery({
    queryKey: ['admin', 'dashboard'] as const,
    queryFn: async () => {
      const res = await api.get<AdminDashboard>('/v0.1/admin/dashboard')
      return res.data
    },
    staleTime: 60_000,
  })
}

export function useApproveUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.post<{ message: string; user_id: string }>(
        `/v0.1/admin/approve/${userId}`
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-users'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] })
    },
  })
}

export function useRejectUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason?: string }) => {
      const res = await api.post<{ message: string; user_id: string }>(
        `/v0.1/admin/reject/${userId}`,
        { reason }
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-users'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] })
    },
  })
}

export function useUsers(status?: string) {
  return useQuery({
    queryKey: ['admin', 'users', { status }] as const,
    queryFn: async () => {
      const res = await api.get<AdminUser[]>('/v0.1/admin/users', {
        params: { status },
      })
      return res.data
    },
    staleTime: 60_000,
  })
}

export function useApprovalTokens() {
  return useQuery({
    queryKey: ['admin', 'approval-tokens'] as const,
    queryFn: async () => {
      const res = await api.get<ApprovalToken[]>('/v0.1/admin/approval-tokens')
      return res.data
    },
    staleTime: 60_000,
  })
}
// ---------------------------------------------------------------------------
// Admin profile & settings
// ---------------------------------------------------------------------------

export interface AdminProfile {
  id: string
  email: string
  full_name: string | null
  status: string
  is_admin: boolean
  created_at: string
  updated_at: string
}

export interface AdminSettings {
  admin_email: string
  app_base_url: string
  total_users: number
  pending_count: number
  approved_count: number
  rejected_count: number
}

export function useAdminProfile() {
  return useQuery({
    queryKey: ['admin', 'profile'] as const,
    queryFn: async () => {
      const res = await api.get<AdminProfile>('/v0.1/admin/profile')
      return res.data
    },
    staleTime: 60_000,
  })
}

export function useUpdateAdminProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (updates: { full_name?: string; email?: string }) => {
      const res = await api.patch<AdminProfile>('/v0.1/admin/profile', updates)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'profile'] })
    },
  })
}

export function useAdminSettings() {
  return useQuery({
    queryKey: ['admin', 'settings'] as const,
    queryFn: async () => {
      const res = await api.get<AdminSettings>('/v0.1/admin/settings')
      return res.data
    },
    staleTime: 60_000,
  })
}

export function useUpdateAdminSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (settings: { admin_email?: string }) => {
      const res = await api.patch<AdminSettings>('/v0.1/admin/settings', settings)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] })
    },
  })
}

export function useChangePassword() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { old_password: string; new_password: string }) => {
      const res = await api.post<{ message: string }>('/v0.1/admin/change-password', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'profile'] })
    },
  })
}
